'use client'

import type { SurveySheetState } from '@/components/embedding/survey/useSurveySheet'
import UnifiedPlayMark from '@/components/embedding/UnifiedPlayMark'
import { attachHlsSource } from '@/lib/hlsPlayer'
import { buildWatermarkUrl } from '@/lib/watermarkLink'
import { ScenarioNodeWithVideoUrl } from '@/orval/generated/public/model'
import React, { FC, RefObject, useCallback, useEffect, useRef } from 'react'
import { DEFAULT_EMBED_FONT, embedFontStack, type EmbedFont } from '@/components/scenario/embedFont'
import PCVideoControls from './PCVideoControls'
import { usePCVideoPlayback } from './usePCVideoPlayback'
import VideoFeedbackScreen from './video-feedback-screen/VideoFeedbackScreen'
import VideoInfoPanel from './video-info-panel'
import VideoPlaylist from './VideoPlaylist'

export interface PCVideoModalProps {
  nodes: ScenarioNodeWithVideoUrl[]
  currentNode: ScenarioNodeWithVideoUrl
  currentVideoIndex: number
  videoRef: RefObject<HTMLVideoElement | null>
  /**
   * マウント起点の自動再生を済ませた動画（nodeKey）。VideoModal が保持する。
   * PC/SP の切替で <video> が作り直されても、視聴者が止めた動画を再生し直さない。
   */
  autoPlayedNodeKeyRef?: { current: string | null }
  showEndScreen: boolean
  onClose: () => void
  onFullClose: () => void
  onPreviousVideo: () => void
  onNextVideo: () => void
  onVideoSelect: (index: number) => void
  onVideoEnd: () => void
  onDismissEndScreen: () => void
  onSkipEndScreen: () => void
  replayAfterEndScreen: boolean
  ctaUrl?: string | null
  ctaLabel?: string | null
  onTrackCtaClick?: (nodeKey: string, url: string, label?: string) => void
  scenarioId?: string
  isPreview?: boolean
  survey?: SurveySheetState
  /** 視聴モーダルのフォント（プレイリスト単位の設定） */
  embedFont?: EmbedFont
}

const PCVideoModal: FC<PCVideoModalProps> = ({
  nodes,
  currentNode,
  currentVideoIndex,
  videoRef,
  autoPlayedNodeKeyRef,
  showEndScreen,
  onClose,
  onFullClose,
  onPreviousVideo,
  onNextVideo,
  onVideoSelect,
  onVideoEnd,
  onDismissEndScreen,
  onSkipEndScreen,
  replayAfterEndScreen,
  ctaUrl,
  ctaLabel,
  onTrackCtaClick,
  scenarioId,
  isPreview = false,
  survey,
  embedFont = DEFAULT_EMBED_FONT,
}) => {
  const hasPrevious = currentVideoIndex > 0
  const hasNext = currentVideoIndex < nodes.length - 1
  const prevShowEndScreenRef = useRef(showEndScreen)

  // 自前コントロール（ネイティブ controls は使わない）。再生速度ボタンを動画上に常時表示し、
  // 動画個別ページと同じ操作体系にする（「…」メニュー経由のワンクッションを無くす）。
  const playback = usePCVideoPlayback({ videoRef })

  const handleTrackCta = useCallback(() => {
    if (onTrackCtaClick && ctaUrl) {
      onTrackCtaClick(currentNode.nodeKey, ctaUrl, ctaLabel ?? undefined)
    }
  }, [onTrackCtaClick, currentNode.nodeKey, ctaUrl, ctaLabel])

  // CVで別タブへ移る間、裏のタブで動画が進み続けないように止める。
  // onPause で isPlaying が false になり、中央の再生マークから再開できる。
  const handlePauseForCta = useCallback(() => {
    const video = videoRef.current
    if (video && !video.paused) video.pause()
  }, [videoRef])

  // Auto-play when transitioning from end screen (feedback done / already voted)
  useEffect(() => {
    if (prevShowEndScreenRef.current && !showEndScreen && replayAfterEndScreen && videoRef.current) {
      const video = videoRef.current
      setTimeout(() => {
        video.currentTime = 0
        video.play()
      }, 100)
    }
    prevShowEndScreenRef.current = showEndScreen
  }, [showEndScreen, replayAfterEndScreen, videoRef])

  // HLS対応: hlsUrl があれば HLS(native/hls.js)で再生、無ければ src(MP4)にフォールバック
  useEffect(() => {
    const video = videoRef.current
    if (!video || !currentNode.hlsUrl) return
    return attachHlsSource(video, {
      hlsUrl: currentNode.hlsUrl,
      mp4Url: currentNode.videoUrl,
      autoPlay: true,
    })
    // 評価画面の間は <video> を外すため、戻ったときに作り直された要素へ HLS を付け直す。
    // 付け直さないと HLS 変換済みの動画は src が空のままで、再生し直せずサムネイルだけになる。
  }, [videoRef, currentNode.hlsUrl, currentNode.videoUrl, showEndScreen])

  // モーダルを開いた時と動画を切り替えた時だけ自動再生する。
  // `autoplay` 属性は動画を読み込み直すたびに再武装される（HTML仕様の media load
  // algorithm が autoplaying flag を true に戻す）ため、別タブから戻って動画が
  // 再ロードされると、視聴者が一時停止したはずの動画が 0:00 から再生されてしまう。
  // 属性には頼らず、ここで一度だけ明示的に再生する。
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    // この動画でマウント起点の自動再生が済んでいるなら繰り返さない。
    // PC/SP の切替で要素が作り直されても、視聴者が止めた動画は止めたまま。
    if (autoPlayedNodeKeyRef?.current === currentNode.nodeKey) return

    let cancelled = false
    const startPlayback = () => {
      if (cancelled) return
      if (autoPlayedNodeKeyRef) {
        autoPlayedNodeKeyRef.current = currentNode.nodeKey
      }
      video.play().catch(() => undefined)
    }

    if (video.readyState >= 3) {
      startPlayback()
    } else {
      video.addEventListener('canplay', startPlayback, { once: true })
    }
    return () => {
      cancelled = true
      video.removeEventListener('canplay', startPlayback)
    }
  }, [videoRef, currentNode.nodeKey, autoPlayedNodeKeyRef])

  return (
    <>
      <style>{`
        .pc-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: fadeIn 300ms ease-out;
          /*
           * 親（shadowHost / container）が popup 表示モードで pointer-events: none を
           * 設定しているケース（parent-bridge 経由の forceOverlay 等）に備え、
           * モーダル本体は明示的にクリックを受け取れるよう auto を上書きする。
           * これが無いと閉じるボタンや動画コントロールがクリックできない。
           */
          pointer-events: auto;
          /*
           * フォントを明示的に固定する（既定はゴシック＝Noto Sans JP、プレイリスト設定で変更可）。
           * Shadow DOM は CSS 変数（--default-font-family 等）を継承するため、
           * 親ページの font-family がモーダル内に漏れて見た目がサイト毎に変わる事象を防ぐ。
           * 固定をやめて inherit にするとこの事象が再発するので、値は必ず embedFontStack から採り、
           * 「先方サイトのフォントを継承」という選択肢は embedFont に持たせない。
           * Web フォント未到着の間は OS 既定でフォールバックし、到着後に swap される。
           */
          font-family: ${embedFontStack(embedFont)};
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }

        .pc-modal-content {
          display: grid;
          grid-template-columns: 120px 1fr 1.1fr;
          gap: 0;
          width: 92%;
          max-width: 1200px;
          height: 92vh;
          max-height: 850px;
          background: #2a2a2a;
          border-radius: 12px;
          overflow: hidden;
          animation: scaleIn 300ms ease-out;
        }

        .pc-modal-content > * {
          min-height: 0;
          overflow: hidden;
        }

        .pc-video-container {
          display: flex;
          flex-direction: column;
          position: relative;
          padding: 8px 0 32px 0;
          overflow: hidden;
        }

        .pc-video-wrapper {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          min-height: 0;
          overflow: hidden;
        }

        .pc-video-area {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }

        /* 動画とその上に重ねるコントロールをまとめる枠。幅は動画の縦横比から決まる
           （height 100% の <video> の固有幅）。元の .pc-video-player が持っていた
           min-width: 85% はこの枠へ移し、動画は枠いっぱいに contain で収める。 */
        .pc-video-frame {
          position: relative;
          display: flex;
          height: 100%;
          max-height: 100%;
          min-width: 85%;
          max-width: 100%;
          border-radius: 12px;
          overflow: hidden;
          background: #2a2a2a;
        }

        .pc-video-player {
          display: block;
          height: 100%;
          width: 100%;
          /* background: #1a1a1a;*/
          background: #2a2a2a;
          object-fit: contain;
          cursor: pointer;
          /* 青い枠線は <video> 自身の border なので、親 .pc-video-frame の角丸と
             overflow: hidden だけでは枠の角が四角く残る。枠線を角丸に沿わせるため、
             親と同じ 12px をここにも持たせる。 */
          border-radius: 12px;
          border: 3px solid var(--video-frame-color, var(--video-accent-color, #1d48af));
          box-sizing: border-box;
        }

        /* 一時停止中の中央再生ボタン（動画個別ページと同じ見た目） */
        .pc-play-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.2);
          border: none;
          padding: 0;
          cursor: pointer;
          z-index: 4;
        }
        .pc-play-overlay-icon {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #1f2937;
        }
        .pc-play-overlay-svg {
          margin-left: 2px;
        }

        .pc-nav-button {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          cursor: pointer;
          transition: all 200ms ease;
          border-radius: 50%;
          z-index: 10;
          background: rgb(0, 0, 0, .7);
        }

        .pc-nav-button.prev {
          left: 4px;
        }

        .pc-nav-button.next {
          right: 4px;
        }

        .pc-nav-button:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.1);
        }

        .pc-nav-button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .pc-nav-icon {
          width: 32px;
          height: 32px;
          color: white;
        }

        .pc-powered-by {
          position: absolute;
          bottom: 10px;
          left: 0;
          right: 0;
          text-align: center;
          color: rgba(255, 255, 255, 0.8);
          font-size: 11px;
          font-family: system-ui, -apple-system, sans-serif;
          text-shadow: 0 0px 12px rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }
        .pc-powered-by-logo {
          height: 14px;
          width: auto;
          display: inline-block;
          filter: drop-shadow(0 0 12px rgba(0, 0, 0, 0.7));
        }
      `}</style>

      <div className="pc-modal-backdrop" onClick={onClose}>
        <div className="pc-modal-content" onClick={(e) => e.stopPropagation()}>
          {/* Left: Video Playlist */}
          <VideoPlaylist nodes={nodes} currentVideoIndex={currentVideoIndex} onVideoSelect={onVideoSelect} />

          {/* Center: Video Player or End Screen */}
          <div className="pc-video-container">
            {showEndScreen ? (
              <VideoFeedbackScreen
                videoId={currentNode.videoId}
                scenarioId={scenarioId}
                variant="pc"
                // 評価の回答後は SP と同じく同じ動画に戻す（次の動画へ自動で進めない）。
                // 動画の終了後に出る画面の操作で別の動画へ進ませる形は作らない（ADR-0023 A-3）。
                onDone={onDismissEndScreen}
                onSkip={onSkipEndScreen}
                trackingEnabled={!isPreview}
              />
            ) : (
              <div className="pc-video-wrapper">
                <div className="pc-video-area">
                  {/* Previous Button - Left side */}
                  <button
                    type="button"
                    className="pc-nav-button prev"
                    onClick={onPreviousVideo}
                    disabled={!hasPrevious}
                    aria-label="Previous video"
                  >
                    <svg className="pc-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                  </button>

                  {/* Video Player + 自前コントロール */}
                  <div className="pc-video-frame">
                    <video
                      ref={videoRef}
                      className="pc-video-player"
                      // hlsUrl 指定時は effect が src を動的設定する(MP4の二重ロード回避)
                      src={currentNode.hlsUrl ? undefined : currentNode.videoUrl}
                      poster={currentNode.thumbnailUrl || undefined}
                      // autoPlay 属性は使わない（再ロードのたびに再武装され、一時停止した
                      // 動画が 0:00 から再生されるため）。自動再生は上の effect が担当する。
                      playsInline
                      muted={playback.isMuted}
                      onClick={playback.togglePlay}
                      onEnded={onVideoEnd}
                      {...playback.videoEventHandlers}
                    />

                    {!playback.isPlaying && (
                      <button
                        type="button"
                        className="pc-play-overlay"
                        onClick={playback.togglePlay}
                        aria-label="再生"
                      >
                        <UnifiedPlayMark />
                      </button>
                    )}

                    <PCVideoControls
                      isMuted={playback.isMuted}
                      playbackRate={playback.playbackRate}
                      currentTime={playback.currentTime}
                      duration={playback.duration}
                      onToggleMute={playback.toggleMute}
                      onCyclePlaybackRate={playback.cyclePlaybackRate}
                      onSeek={playback.seekTo}
                      formatTime={playback.formatTime}
                    />
                  </div>

                  {/* Next Button - Right side */}
                  <button
                    type="button"
                    className="pc-nav-button next"
                    onClick={onNextVideo}
                    disabled={!hasNext}
                    aria-label="Next video"
                  >
                    <svg className="pc-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            <a
              className="pc-powered-by"
              href={buildWatermarkUrl('embed_modal')}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Powered by moovy studio"
            >
              <span>Powered by</span>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                className="pc-powered-by-logo"
                src="https://studio.moovy.jp/images/logo-moovystudio-white.png"
                alt="moovy studio"
                width={84}
                height={14}
              />
            </a>
          </div>

          {/* Right: Video Info Panel */}
          <VideoInfoPanel
            currentNode={currentNode}
            onClose={onFullClose}
            ctaUrl={ctaUrl}
            ctaLabel={ctaLabel}
            onTrackCtaClick={handleTrackCta}
            onPauseVideo={handlePauseForCta}
            survey={survey}
          />
        </div>
      </div>
    </>
  )
}

export default React.memo(PCVideoModal)
